/* eslint-disable unicorn/consistent-destructuring */
/* eslint-disable @typescript-eslint/no-empty-function */
import { FRUser} from '@forgerock/javascript-sdk';
import { getCurrentEnv } from '../hooks/apiUrlResolver';
import { trackEvent } from '../components/Utils/splunkEventHelper';
import sessionApi from '../api/ic/sessionApi';
import authConstants from './authConstants';
import { isLocal } from './authUtils';

const {
  AXI_EXPIRATION_TIMER,
  ACTIVITY_SYNC_KEY,
  AUTH_STATE_KEY,
  getNewWarningTimer,
  getNewSessionTimer,
  getNextResetTimer
} = authConstants;

// SessionStore interface
export interface SessionStore {
  isAuthenticated: boolean | null;
  showWarning: boolean;
  warningTime: number;
  sessionExpiry: number;
  lastActivityTime: string | null;
  user: Record<string, unknown> | null;
  setIsAuthenticated: (value: boolean | null) => void;
  setShowWarning: (value: boolean) => void;
  setWarningTime: (value: number) => void;
  setSessionExpiry: (value: number) => void;
  setLastActivityTime: (value: string | null) => void;
  setUser: (userData: Record<string, unknown> | null) => void;
}
const setCookieValue = (name: string, value: string, options: string) => {
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${name}=${value}; ${options}`;
};

// Timer reset throttling
let nextResetTime = 0;

// Handle expired session
const handleExpiredSession = (sessionStore: SessionStore) => {
  sessionStore.setIsAuthenticated(false);
  sessionStore.setWarningTime(0);
  sessionStore.setSessionExpiry(0);
};

// Session validation
const validateSession = async (sessionStore: SessionStore) => {
  try {
    const response = await sessionApi.refreshSession();
    
    // if session is expired, data is an HTML page, if session is active, data is an empty string
    if (response?.status === 200 && !(response as { data?: unknown }).data) {
      sessionStore.setIsAuthenticated(true);
      return true;
    }
    handleExpiredSession(sessionStore);
    return false;
  } catch {
    trackEvent({
      event: 'session-validation-failed',
      api: 'session-refresh-api',
    });
    handleExpiredSession(sessionStore);
    return false;
  }
};

// Activity tracking with throttling
const resetActivityTimer = (sessionStore: SessionStore) => {
  if (!sessionStore.isAuthenticated || nextResetTime >= Date.now()) return;
  
  const currentTime = Date.now();
  nextResetTime = getNextResetTimer();

  const now = currentTime.toString();
  localStorage.setItem(ACTIVITY_SYNC_KEY, now);
  
  sessionStore.setLastActivityTime(now);
  sessionStore.setWarningTime(getNewWarningTimer(currentTime));
  sessionStore.setSessionExpiry(getNewSessionTimer(currentTime));
  sessionStore.setShowWarning(false);
};

// Enhanced logout function with ForgeRock integration
export const logout = async (sessionStore: SessionStore) => {
  try {
    await FRUser.logout();
  } catch {
    trackEvent({
      event: 'forgerock-logout-failed',
      api: 'forgerock-auth-api',
    });
    throw new Error('Logout failed');
  }
  // Signal logout to other tabs via localStorage
  localStorage.setItem(AUTH_STATE_KEY, 'logged-out');
  
  // Set F5 load balancer cookies (critical for proper logout)
  const currentEnv = getCurrentEnv();
  switch (currentEnv) {
    case 'prd':
      setCookieValue('BIGipServer~edgeservice-north~north-prd-apps-pool-ag-443', 'rd3050o00000000000000000000ffff0a473034o443', 'path=/; Secure; SameSite=Strict');
      setCookieValue('BIGipServer~edgeservice-north~north-prd-apps-pool-dt-443', 'rd3050o00000000000000000000ffff0a463034o443', 'path=/; Secure; SameSite=Strict');
      break;
    case 'qap':
      setCookieValue('BIGipServer~edgeservice-north~north-apps-qap-pool-ag-443', 'rd3050o00000000000000000000ffff0a4830dfo443', 'path=/; Secure; SameSite=Strict');
      break;
    case 'qua':
      setCookieValue('BIGipServer~edgeservice-north~north-apps-qua-pool-ag-443', 'rd3050o00000000000000000000ffff0a4830deo443', 'path=/; Secure; SameSite=Strict');
      break;
    case 'tst':
      setCookieValue('BIGipServer~edgeservice-north~north-apps-tst-pool-ag-443', 'rd3050o00000000000000000000ffff0a4830ddo443', 'path=/; Secure; SameSite=Strict');
      break;
    default:
      break;
  }
  
  // Clear state AFTER logout completes
  sessionStore.setIsAuthenticated(null);
  sessionStore.setUser(null);
  sessionStore.setShowWarning(false);
  sessionStore.setWarningTime(0);
  sessionStore.setSessionExpiry(0);
  
  // Clear session storage
  localStorage.removeItem(ACTIVITY_SYNC_KEY);

  // Reload to reset app state
  window.location.reload();
};

// Cross-tab sync: handles login, logout, and activity synchronization
const setupCrossTabSync = (sessionStore: SessionStore) => {
  const handleStorageChange = (event: StorageEvent) => {
    // Only process events for keys we need
    if (event.key !== AUTH_STATE_KEY && event.key !== ACTIVITY_SYNC_KEY) return;
    
    if (event.key === AUTH_STATE_KEY) {
      if (event.newValue === 'logged-out') {
        sessionStore.setIsAuthenticated(false);
        sessionStore.setUser(null);
        window.location.reload();
      } else if (event.newValue === 'logged-in' && !sessionStore.isAuthenticated) {
        window.location.reload();
      }
    } else if (event.newValue && sessionStore.isAuthenticated && sessionStore.lastActivityTime !== event.newValue) {
      nextResetTime = getNextResetTimer();
      const activityTime = Number.parseInt(event.newValue, 10);
      sessionStore.setWarningTime(getNewWarningTimer(activityTime));
      sessionStore.setSessionExpiry(getNewSessionTimer(activityTime));
      sessionStore.setLastActivityTime(event.newValue);
      sessionStore.setShowWarning(false);
    }
  };
  
  window.addEventListener('storage', handleStorageChange);
  
  return { cleanup: () => window.removeEventListener('storage', handleStorageChange) };
};

/* eslint-disable consistent-return */
export const initializeSession = (sessionStore: SessionStore): (() => void) | void => {
  // Setup cross-tab synchronization
  const { cleanup } = setupCrossTabSync(sessionStore);

  // Initial session check
  (async () => {
    
      // eslint-disable-next-line unicorn/no-document-cookie
    const hasSessionCookie = document.cookie.includes(authConstants.SESSION_COOKIE_NAME);

    if (!hasSessionCookie && !isLocal()) {
      sessionStore.setIsAuthenticated(false);
      return;
    }

    await validateSession(sessionStore);
    resetActivityTimer(sessionStore);
    // Signal login to other tabs if authenticated
    if (sessionStore.isAuthenticated) {
      localStorage.setItem(AUTH_STATE_KEY, 'logged-in');
    }
  })();
  sessionStore.setLastActivityTime(localStorage.getItem(ACTIVITY_SYNC_KEY));

  // Session timeout monitoring
  const checkTimers = () => {
    if (!sessionStore.isAuthenticated) return;
    
    const now = Date.now();
    
    if (sessionStore.sessionExpiry > 0 && now >= sessionStore.sessionExpiry) {
      sessionStore.setShowWarning(false);
      // eslint-disable-next-line promise/catch-or-return
      (async () => {
        await logout(sessionStore);
      })().catch(() => {
        // logout failed
      });
      return;
    }
    
    if (sessionStore.warningTime > 0 && now >= sessionStore.warningTime && !sessionStore.showWarning) {
      sessionStore.setShowWarning(true);
    }
  };

  const sessionInterval = setInterval(checkTimers, 1000);

  // Activity listeners
  const handleMouseDown = () => {
    if (!sessionStore.isAuthenticated || sessionStore.showWarning) return;
    resetActivityTimer(sessionStore);
  };
  const handleKeyDown = () => {
    if (!sessionStore.isAuthenticated || sessionStore.showWarning) return;
    resetActivityTimer(sessionStore);
  };
  
  document.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('keydown', handleKeyDown);

  // AXI validation timer - only run when authenticated
  const axiValidationInterval = setInterval(async () => {
    if (!sessionStore.isAuthenticated) return;
    
    const lastActivity = localStorage.getItem(ACTIVITY_SYNC_KEY);
    if (lastActivity && Date.now() - Number(lastActivity) <= 20 * 60 * 1000) {
      await validateSession(sessionStore);
    }
  }, AXI_EXPIRATION_TIMER);

  // Return cleanup function
  return () => {
    clearInterval(sessionInterval);
    clearInterval(axiValidationInterval);
    document.removeEventListener('mousedown', handleMouseDown);
    document.removeEventListener('keydown', handleKeyDown);
    cleanup();
  };
};